// ✅ src/pages/VehicleV2/MonthlyDieselReport_Print.jsx
// Monthly Diesel Report (MONTH-WISE GRID)
// Backend rows sample fields:
//  VehicleMainType, Branch, VehicleRegNo, VehicleName, fld_vehiclemaintypeid,
//  January..December, Total, Average

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa6";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/dieselConsumption";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const safeDate = (v) => {
  const s = toStr(v);
  if (!s || isSelect(s)) return "";
  return s;
};

export default function MonthlyDieselReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const passed = location?.state || {};
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const q = (key) => toStr(qs.get(key));

  const filters = useMemo(() => {
    return {
      vehtype: toStr(passed?.vehtype || q("vehtype")),
      branch: toStr(passed?.branch || q("branch") || q("branchid")),
      vehbasis: toStr(passed?.vehbasis || q("vehbasis") || q("basis")),
      month: toStr(passed?.month || q("month")),
      year: toStr(passed?.year || q("year")),

      fromdate: safeDate(passed?.fromDate || passed?.fromdate || q("fromDate") || q("fromdate")),
      todate: safeDate(passed?.toDate || passed?.todate || q("toDate") || q("todate")),

      vehregno: toStr(passed?.vehRegNo || passed?.vehregno || q("vehRegNo") || q("vehregno") || q("regno")),
      vehcode: toStr(passed?.vehicleCode || passed?.vehcode || q("vehicleCode") || q("vehcode")),
      vehname: toStr(passed?.vehicleName || passed?.vehname || q("vehicleName") || q("vehname")),

      fld_vehicletypeid: toStr(passed?.vehicleMainType || passed?.fld_vehicletypeid || q("vehicleMainType")),
      loginuserid: Number(sessionStorage.getItem("loginuserid")) || 0,
      branchType: toStr(sessionStorage.getItem("branchtype") || ""),
    };
  }, [passed, qs]);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  const monthCols = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  const load = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      const cleaned = {
        ...filters,
        branch: isSelect(filters.branch) ? "" : filters.branch,
        vehbasis: isSelect(filters.vehbasis) ? "" : filters.vehbasis,
        fld_vehicletypeid: isSelect(filters.fld_vehicletypeid) ? "" : filters.fld_vehicletypeid,
      };

      const { data } = await axios.get(`${API_BASE}/monthlyIssue`, { params: cleaned });
      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    try {
      setExporting(true);

      const cleaned = {
        ...filters,
        branch: isSelect(filters.branch) ? "" : filters.branch,
        vehbasis: isSelect(filters.vehbasis) ? "" : filters.vehbasis,
        fld_vehicletypeid: isSelect(filters.fld_vehicletypeid) ? "" : filters.fld_vehicletypeid,
      };

      const { data } = await axios.get(`${API_BASE}/exportPdf`, { params: cleaned });
      if (!data?.success) throw new Error(data?.message || "Failed to export PDF");
      if (data?.url) window.open(data.url, "_blank");
      else toast.success(`PDF created: ${data?.fileName || "Monthly Diesel Report"}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // footer totals across all vehicles (optional)
  const footerTotals = useMemo(() => {
    const sums = {};
    monthCols.forEach((m) => (sums[m] = 0));
    let totalSum = 0;
    let avgSum = 0;
    let avgCount = 0;

    for (const r of rows) {
      monthCols.forEach((m) => {
        const n = safeNum(r?.[m]);
        if (n !== "") sums[m] += n;
      });

      const t = safeNum(r?.Total);
      if (t !== "") totalSum += t;

      const a = safeNum(r?.Average);
      if (a !== "") {
        avgSum += a;
        avgCount += 1;
      }
    }

    return {
      months: sums,
      totalSum,
      avgAll: avgCount ? Math.round((avgSum / avgCount) * 100) / 100 : "",
    };
  }, [rows, monthCols]);

  const showBranch = isSelect(filters.branch) ? "-" : (filters.branch || "-");

  return (
    <div className={`${isDark ? "bg-gray-900" : "bg-gray-100"} min-h-screen`}>
      
        <div className="w-full bg-white">
          <style>{`
            @page { size: A4 landscape; margin: 8mm; }
            @media print {
              body { background: #fff !important; }
              .no-print { display: none !important; }
              .print-area { margin: 0 !important; padding: 0 !important; }
            }
          `}</style>

          {/* Toolbar */}
          <div className="no-print flex items-center justify-between px-4 py-3 border-b bg-white">
            <div className="text-sm">
              {loading ? "Loading..." : errMsg ? <span className="text-red-600">{errMsg}</span> : `Records: ${rows.length}`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <FaArrowLeft /> Back
              </button>

              <button
                onClick={() => window.print()}
                disabled={loading || !rows.length}
                className={`bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60`}
              >
                <FaPrint /> Print
              </button>

              <button
                onClick={exportPdf}
                disabled={exporting || loading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2 disabled:opacity-60"
              >
                <FaFilePdf /> {exporting ? "Exporting..." : "PDF"}
              </button>
            </div>
          </div>

          {/* Print Area */}
          <div className="print-area mx-auto max-w-[1100px] p-4 print:p-0">
            <div className="border-2 border-black p-3">
              <div className="border border-black">
                {/* Header */}
                <div className="border-b border-black p-2">
                  <div className="flex items-center gap-3">
                    <div className="w-[110px] h-[80px] border border-black flex items-center justify-center">
                      <div className="text-[10px] font-bold text-center leading-3">
                        UNDER
                        <br />
                        CONSTRUCTION
                      </div>
                    </div>

                    <div className="flex-1 text-center leading-5">
                      <div className="font-bold text-lg">Nissi Infotech</div>
                      <div className="font-semibold text-sm">Nissi</div>
                      <div className="font-semibold text-sm">CIVIL CONSTRUCTION</div>
                      <div className="text-[11px] mt-1">
                        3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                      </div>
                      <div className="text-[11px]">
                        Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
                      </div>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="border-b border-black text-center font-bold py-2 text-sm">MONTHLY DIESEL REPORT</div>

                {/* Filter info */}
                <div className="border-b border-black p-2 text-[12px]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div>
                        <span className="font-semibold">Vehicle Type</span> : {filters.vehtype || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Branch</span> : {showBranch}
                      </div>
                      <div>
                        <span className="font-semibold">Month / Year</span> : {filters.month || "-"} / {filters.year || "-"}
                      </div>
                    </div>

                    <div className="text-right">
                      <div>
                        <span className="font-semibold">Reg No</span> : {filters.vehregno || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">Vehicle</span> : {filters.vehcode || "-"} / {filters.vehname || "-"}
                      </div>
                      <div>
                        <span className="font-semibold">From</span> : {filters.fromdate || "-"} &nbsp;&nbsp;
                        <span className="font-semibold">To</span> : {filters.todate || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table (Month-wise) */}
                <div className="p-2 overflow-x-auto">
                  <table className="w-full border border-black border-collapse text-[11px]">
                    <thead>
                      <tr>
                        <th className="border border-black px-2 py-1">S.No</th>
                        <th className="border border-black px-2 py-1">Vehicle Main Type</th>
                        <th className="border border-black px-2 py-1">Branch</th>
                        <th className="border border-black px-2 py-1">Reg No</th>
                        <th className="border border-black px-2 py-1">Vehicle Name</th>

                        {monthCols.map((m) => (
                          <th key={m} className="border border-black px-2 py-1 text-right">
                            {m.slice(0, 3)}
                          </th>
                        ))}

                        <th className="border border-black px-2 py-1 text-right">Total</th>
                        <th className="border border-black px-2 py-1 text-right">Average</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!loading && rows.length === 0 ? (
                        <tr>
                          <td className="border border-black px-2 py-2 text-center" colSpan={5 + monthCols.length + 2}>
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={`${r?.VehicleRegNo || ""}-${r?.VehicleName || ""}-${idx}`}>
                            <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                            <td className="border border-black px-2 py-1">{toStr(r?.VehicleMainType) || "-"}</td>
                            <td className="border border-black px-2 py-1">{toStr(r?.Branch) || "-"}</td>
                            <td className="border border-black px-2 py-1">{toStr(r?.VehicleRegNo) || "-"}</td>
                            <td className="border border-black px-2 py-1">{toStr(r?.VehicleName) || "-"}</td>

                            {monthCols.map((m) => (
                              <td key={m} className="border border-black px-2 py-1 text-right">
                                {r?.[m] === null || r?.[m] === undefined || r?.[m] === "" ? "-" : r?.[m]}
                              </td>
                            ))}

                            <td className="border border-black px-2 py-1 text-right">
                              {r?.Total === null || r?.Total === undefined || r?.Total === "" ? "-" : r?.Total}
                            </td>
                            <td className="border border-black px-2 py-1 text-right">
                              {r?.Average === null || r?.Average === undefined || r?.Average === "" ? "-" : r?.Average}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {/* Optional footer totals across all vehicles */}
                    {rows.length > 0 && (
                      <tfoot>
                        <tr>
                          <td className="border border-black px-2 py-1 font-semibold text-right" colSpan={5}>
                            Grand Total
                          </td>

                          {monthCols.map((m) => (
                            <td key={m} className="border border-black px-2 py-1 font-semibold text-right">
                              {footerTotals.months[m] || ""}
                            </td>
                          ))}

                          <td className="border border-black px-2 py-1 font-semibold text-right">
                            {footerTotals.totalSum || ""}
                          </td>
                          <td className="border border-black px-2 py-1 font-semibold text-right">
                            {footerTotals.avgAll !== "" ? footerTotals.avgAll : ""}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* Footer */}
                <div className="border-t border-black p-2">
                  <div className="border border-black p-2 text-[11px]">
                    Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
                  </div>
                </div>
              </div>
            </div>

            <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
          </div>
        </div>
     
    </div>
  );
}